import { useCallback, useEffect, useState } from 'react';
import { reelService } from 'api/services/ReelService';
import type { ReelViewModel } from 'api/types/reel';

function normalizeSavedReel(raw: unknown): ReelViewModel {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    id: String(o.id ?? o.Id ?? '').trim(),
    videoUrl: String(o.videoUrl ?? o.VideoUrl ?? '').trim(),
    userId: String(o.userId ?? o.UserId ?? '').trim(),
    createdAt: (() => {
      const v = o.createdAt ?? o.CreatedAt ?? o.createdAtUtc ?? o.CreatedAtUtc;
      return typeof v === 'string' ? v : null;
    })(),
    listingId: (() => {
      const v = o.listingId ?? o.ListingId;
      return v == null ? null : String(v);
    })(),
    isSaved: true,
  };
}

export interface UseSavedReelsResult {
  reels: ReelViewModel[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  removeReel: (reelId: string) => void;
}

export function useSavedReels(enabled: boolean): UseSavedReelsResult {
  const [reels, setReels] = useState<ReelViewModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!enabled) {
      setReels([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await reelService.getSaved();
      if (!result.isSuccess) {
        setError(result.message ?? 'Kaydedilen gönderiler yüklenemedi.');
        setReels([]);
        return;
      }
      const raw = result.data;
      const items = Array.isArray(raw) ? raw.map(normalizeSavedReel) : [];
      setReels(items.filter((item) => item.id && item.videoUrl));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bir hata oluştu.');
      setReels([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const removeReel = useCallback((reelId: string) => {
    setReels((prev) => prev.filter((item) => item.id !== reelId));
  }, []);

  return { reels, loading, error, refetch, removeReel };
}
